import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import instance from "../../store/actions/instance";
import {
  Modal,
  Select,
  Input,
  Col,
  Button,
  message,
  Table,
  Tag,
  Space,
  Drawer,
  Spin,
  Badge,
  ConfigProvider,
  Upload,
  Image,
  Progress,
  Tooltip,
  Divider,
} from "antd";
import {
  PaperClipOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  CloseCircleFilled,
  SendOutlined,
  PictureOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { ReactComponent as SettingOutlined } from "../Support/assets/settings.svg";
import { ReactComponent as UserOutlined } from "../Support/assets/user.svg";
import { ReactComponent as SmileOutlined } from "../Support/assets/message-smile-square.svg";

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;
const DESCRIPTION_MAX_LENGTH = 2000;

function ellipsis(str, max = 45) {
  if (str == null) return "";
  const s = String(str);
  return s.length > max ? s.slice(0, max).replace(/\s+$/, "") + "…" : s;
}

const FIRST_RESPONSE_OPTIONS = [
  "Within 2 hours",
  "Within working hours",
  "Relaxed",
];

const TOPICS = [
  "Online store",
  "Dashboard experience",
  "POS",
  "B2B",
  "Table ordering",
  "QR menus",
  "ordable/ manager",
  "ordable/ driver app",
  "Other",
];

const SUBTOPICS = {
  "Online store": [
    "Website performance",
    "Website theme and design",
    "Website domain",
    "Promotions",
    "Products and categories",
    "Delivery time",
    "Payments",
    "Gifting",
    "Cart",
    "Options and modifiers",
    "Variants",
    "Order tracking",
    "Loyalty points and wallet",
    "Other",
  ],
  "Dashboard experience": [
    "Dashboard performance",
    "Order management",
    "Notifications",
    "Product management",
    "Printing and invoices",
    "Driver management",
    "Reports",
    "Settings",
    "Customers and feedback",
    "Billing",
    "Addons",
    "Branch management",
    "Payment integrations",
    "Delivery integrations",
    "Social integrations",
    "Inventory and POS integrations",
    "Promotions",
    "Abandoned carts",
    "Messaging",
    "Other",
  ],
};

const SUPPORT_TOPICS = [
  "Onboarding & setup",
  "Billing & invoices",
  "Plan or pricing changes",
  "Account settings",
  "Business advice",
  "Something else",
];

// ===========================
// STATUS SETS (Tech vs Support)
// ===========================
const TECH_STATUSES = [
  "Under Review",
  "Accepted",
  "In Progress",
  "Released",
  "Rejected",
];
const SUPPORT_STATUSES = [
  "Under Review",
  "Contacted",
  "Rejected",
  "In Progress",
  "Done",
];

const TECH_STATUS_COLOR = {
  "Under Review": "gold",
  Accepted: "geekblue",
  "In Progress": "blue",
  Released: "green",
  Rejected: "",
};

const SUPPORT_STATUS_COLOR = {
  "Under Review": "gold",
  Contacted: "geekblue",
  Closed: "",
  "In Progress": "blue",
  Done: "green",
};

const isSupportDept = (department) => department !== "tech";

const getStatusesForDept = (department) =>
  isSupportDept(department) ? SUPPORT_STATUSES : TECH_STATUSES;

const getStatusColor = (department, status) =>
  (isSupportDept(department) ? SUPPORT_STATUS_COLOR : TECH_STATUS_COLOR)[
    status
  ] || (isSupportDept(department) ? "default" : "default");

const isImage = (mime = "", name = "") =>
  /^image\//.test(mime) || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || "");

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

// ===========================
// Chat disabled statuses
// ===========================
const isChatClosedStatus = (status) => status === "Rejected";

class FeatureRequestModal extends Component {
  messagesWrapRef = createRef();

  state = {
    topic: undefined,
    subtopic: undefined,
    description: "",
    priority: "Low",
    first_response: undefined,
    submitting: false,
    type: "feature",
    activeTab: this.props.history?.location?.pathname?.includes("/requests/new")
      ? "new"
      : this.props.history?.location?.pathname?.includes("/requests/all")
      ? "list"
      : "new",
    loadingRequests: false,
    requests: [],
    convoVisible: false,
    convoLoading: false,
    convoMessages: [],
    selectedTicket: null,
    composer: "",
    sending: false,
    viewportWidth: typeof window !== "undefined" ? window.innerWidth : 1024,
    viewportHeight: typeof window !== "undefined" ? window.innerHeight : 768,
    autoScrollOnOpen: false,
    users: [],
    selectedUser: undefined,
    lastPath: this.props.history?.location?.state?.lastPath,

    // Chat attachments (message-level)
    pendingAttachments: [],
    uploadProgress: {},
    dragOver: false,

    // Request-level (initial) attachments for the form
    pendingRequestAttachments: [],
    requestUploadProgress: {},

    // Drawer: request-level attachments returned by API for selected ticket
    initialAttachments: [],

    // Status updating (staff editing support tickets)
    statusUpdating: {},
  };

  fetchUsers = async () => {
    try {
      const res = await instance.get("/fetch_users_by_staff/");
      this.setState({ users: res.data });
    } catch (e) {
      console.error(e);
    }
  };

  componentDidMount = async () => {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.onResize);
      this.onResize();
    }

    if (this.props.match?.params?.id) {
      await this.fetchRequests();
      const req = this.state.requests.filter(
        (x) => x.key === this.props.match.params.id
      );
      if (req.length) this.openConversation(req[0]);
      else this.props.history.replace("/manage/requests/all");
    } else if (
      this.props.history?.location?.pathname?.includes("requests/all")
    ) {
      this.fetchRequests();
    }
    this.fetchUsers();
  };

  componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.onResize);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.convoVisible && !prevState.convoVisible) {
      requestAnimationFrame(() => this.scrollMessagesToBottom());
    }
    if (
      this.state.convoVisible &&
      prevState.convoLoading &&
      !this.state.convoLoading &&
      this.state.autoScrollOnOpen
    ) {
      this.scrollMessagesToBottom();
      this.setState({ autoScrollOnOpen: false });
    }
  }

  onResize = () => {
    this.setState({
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
  };

  // ===========================
  // Staff: change support status
  // ===========================
  changeRequestStatus = async (record, newStatus) => {
    const { t } = this.props;
    const id = record?.raw_id;
    if (!id) return;

    if (!this.props.is_staff || !isSupportDept(record.department)) return;

    this.setState((prev) => ({
      statusUpdating: { ...prev.statusUpdating, [id]: true },
    }));

    try {
      const res = await instance.post("/change_request_status/", {
        id,
        status: newStatus,
      });

      if (res.data?.success) {
        this.setState((prev) => ({
          requests: prev.requests.map((r) =>
            r.raw_id === id
              ? {
                  ...r,
                  status: newStatus,
                  _raw: { ...r._raw, status: newStatus },
                }
              : r
          ),
          selectedTicket:
            prev.selectedTicket?.raw_id === id
              ? {
                  ...prev.selectedTicket,
                  status: newStatus,
                  _raw: { ...prev.selectedTicket._raw, status: newStatus },
                }
              : prev.selectedTicket,
        }));
        message.success(t("Status updated"));
      } else {
        message.error(res.data?.message || t("SomethingWentWrong"));
      }
    } catch (e) {
      console.error(e);
      message.error(t("Failed to update status"));
    } finally {
      this.setState((prev) => {
        const next = { ...prev.statusUpdating };
        delete next[id];
        return { statusUpdating: next };
      });
    }
  };

  // ===========================
  // Submit (request form)
  // ===========================
  requiresSubtopic = (topic) => Array.isArray(SUBTOPICS[topic]);

  handleSubmit = async () => {
    const {
      topic,
      subtopic,
      description,
      priority,
      first_response,
      type,
      selectedUser,
      pendingRequestAttachments,
    } = this.state;
    const { t, config } = this.props;

    if (!topic || !description?.trim() || !priority) {
      return message.warning(
        t("Please fill in topic, description and priority.")
      );
    }
    if (this.requiresSubtopic(topic) && !subtopic) {
      return message.warning(t("Please select a sub-topic."));
    }

    const attachment_tokens = pendingRequestAttachments.map(
      (a) => a.token || a.id
    );

    const isSupport = this.props.history?.location?.pathname?.includes(
      "/requests/new_support"
    );
    const payload = {
      topic,
      subtopic: this.requiresSubtopic(topic) ? subtopic : null,
      description: description.trim(),
      priority,
      first_response,
      type,
      selectedUser,
      attachment_tokens,
      language: config.language,
      isSupport: isSupport,
    };

    try {
      this.setState({ submitting: true });

      const res = await instance.post("/submit_request/", payload);
      if (res.data?.success) {
        message.success(t("Your feature request has been submitted."));
        this.setState({
          topic: undefined,
          subtopic: undefined,
          description: "",
          priority: undefined,
          submitting: false,
          first_response: undefined,
          type: "feature",
          selectedUser: undefined,
          pendingRequestAttachments: [],
          requestUploadProgress: {},
        });
        this.setState({ activeTab: "list" }, () => {
          this.fetchRequests();
          this.props.history.push("/manage/requests/all");
        });
      } else {
        this.setState({ submitting: false });
        message.error(res.data?.message || t("SomethingWentWrong"));
      }
    } catch (e) {
      this.setState({ submitting: false });
      message.error(t("Failed to submit. Please try again."));
    }
  };

  // ===========================
  // Requests list + messages
  // ===========================
  fetchRequests = async () => {
    const { t } = this.props;
    try {
      this.setState({ loadingRequests: true });
      let { data } = await instance.get("/fetch_submitted_requests/");
      data = data.data;

      const rows = data.map((x) => ({
        key: x.tracking_id,
        raw_id: x.tracking_id,
        tracking_id: `[${x.tracking_id}] ${ellipsis(x.description)}`,
        created: x.created,
        modified: x.modified,
        department: x.department,
        status: x.status,
        _raw: x,
        has_notifications: x.has_notifications,
      }));

      const anyHasNotifications = rows.filter((x) => x.has_notifications);
      if (!anyHasNotifications.length) {
        this.props.requestsNotificationsOff();
      }
      this.setState({ requests: rows, loadingRequests: false });
    } catch (e) {
      this.setState({ loadingRequests: false });
      message.error(t("Failed to load your requests."));
    }
  };

  openConversation = async (record) => {
    this.setState(
      {
        convoVisible: true,
        selectedTicket: record,
        convoLoading: true,
        convoMessages: [],
        autoScrollOnOpen: true,
        composer: "",
        pendingAttachments: [],
        uploadProgress: {},
        initialAttachments: [],
      },
      this.fetchMessages
    );
  };

  closeConversation = () => {
    this.setState({
      convoVisible: false,
      selectedTicket: null,
      convoMessages: [],
      composer: "",
      sending: false,
      pendingAttachments: [],
      uploadProgress: {},
      initialAttachments: [],
    });
    this.props.history.replace("/manage/requests/all");
  };

  fetchMessages = async () => {
    const { t } = this.props;
    const { selectedTicket } = this.state;
    if (!selectedTicket?.raw_id) return;

    try {
      this.setState({ convoLoading: true });
      const { data } = await instance.get("/fetch_request_messages/", {
        params: { tracking_id: selectedTicket.raw_id },
      });

      const arr = Array.isArray(data?.data?.messages)
        ? data.data.messages
        : Array.isArray(data?.data)
        ? data.data
        : [];

      const initialAttachments = data?.data?.initial_attachments || [];

      const messages = arr.map((m, idx) => ({
        key: m.id || idx,
        created: m.created,
        sender: m.sender,
        isClient: m.is_client ?? m["is client"],
        message: m.message,
        attachments: m.attachments || [],
      }));

      this.setState(
        { convoMessages: messages, initialAttachments, convoLoading: false },
        this.scrollMessagesToBottom
      );
    } catch (e) {
      this.setState({ convoLoading: false });
      message.error(t("Failed to load messages."));
    }

    let newRequests = this.state.requests;
    let index = newRequests.findIndex(
      (x) => x.raw_id === selectedTicket?.raw_id
    );
    if (index >= 0) {
      newRequests[index].has_notifications = false;
      this.setState({ requests: newRequests });
    }
    if (!newRequests.map((x) => x.has_notifications).length) {
      this.props.requestsNotificationsOff();
    }
  };

  scrollMessagesToBottom = () => {
    const el = this.messagesWrapRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  // ===========================
  // Attachments (message-level)
  // ===========================
  uploadSingleFile = async (file, onProgress) => {
    const { t } = this.props;
    const { selectedTicket } = this.state;

    // No attaching when chat disabled
    if (isChatClosedStatus(selectedTicket?.status)) {
      message.warning(t("This request is closed."));
      return Promise.reject();
    }

    if (!selectedTicket?.raw_id) {
      message.warning(t("Open a ticket to attach files."));
      return Promise.reject();
    }

    const form = new FormData();
    form.append("file", file);
    form.append("tracking_id", selectedTicket.raw_id);

    const res = await instance.post("/upload_attachment/", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        const percent = Math.round((evt.loaded * 100) / (evt.total || 1));
        onProgress?.({ percent });
        this.setState((prev) => ({
          uploadProgress: { ...prev.uploadProgress, [file.uid]: percent },
        }));
      },
    });

    const meta = res?.data?.data || {
      id: res?.data?.id,
      token: res?.data?.token,
      url: res?.data?.url,
      name: res?.data?.name || file.name,
      size: res?.data?.size || file.size,
      mime: res?.data?.mime || file.type,
    };

    const attachment = {
      uid: file.uid,
      id: meta.id,
      token: meta.token,
      url: meta.url,
      name: meta.name,
      size: meta.size,
      mime: meta.mime,
      progress: 100,
    };

    this.setState((prev) => ({
      pendingAttachments: [...prev.pendingAttachments, attachment],
    }));

    return attachment;
  };

  handleCustomUpload = async ({ file, onProgress, onError, onSuccess }) => {
    const { t } = this.props;
    try {
      await this.uploadSingleFile(file, onProgress);
      onSuccess?.("ok");
      message.success(t("Uploaded"));
    } catch (err) {
      console.error(err);
      onError?.(err);
      message.error(t("Upload failed"));
    }
  };

  removePendingAttachment = (uid) => {
    this.setState((prev) => ({
      pendingAttachments: prev.pendingAttachments.filter((a) => a.uid !== uid),
    }));
  };

  onPasteIntoComposer = (e) => {
    const { selectedTicket } = this.state;

    if (isChatClosedStatus(selectedTicket?.status)) return;

    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    files.forEach((f) =>
      this.handleCustomUpload({
        file: Object.assign(f, { uid: `${Date.now()}-${Math.random()}` }),
      })
    );
  };

  onDropOnComposer = (e) => {
    const { selectedTicket } = this.state;

    if (isChatClosedStatus(selectedTicket?.status)) return;

    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    files.forEach((f) =>
      this.handleCustomUpload({
        file: Object.assign(f, { uid: `${Date.now()}-${Math.random()}` }),
      })
    );
    this.setState({ dragOver: false });
  };

  // ===========================
  // Attachments (request-level during submit)
  // ===========================
  handleInitialUpload = async ({ file, onProgress, onError, onSuccess }) => {
    const { t } = this.props;
    try {
      const form = new FormData();
      form.append("file", file);

      const res = await instance.post(
        "/upload_request_attachment_draft/",
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const percent = Math.round((evt.loaded * 100) / (evt.total || 1));
            onProgress?.({ percent });
            this.setState((prev) => ({
              requestUploadProgress: {
                ...prev.requestUploadProgress,
                [file.uid]: percent,
              },
            }));
          },
        }
      );

      const meta = res?.data?.data || {};
      const att = {
        uid: file.uid,
        id: meta.id,
        token: meta.token,
        url: meta.url,
        name: meta.name || file.name,
        size: meta.size || file.size,
        mime: meta.mime || file.type,
        progress: 100,
      };

      this.setState((prev) => ({
        pendingRequestAttachments: [...prev.pendingRequestAttachments, att],
      }));

      onSuccess?.("ok");
      message.success(t("Uploaded"));
    } catch (err) {
      console.error(err);
      onError?.(err);
      message.error(t("Upload failed"));
    }
  };

  removeInitialPending = (uid) => {
    this.setState((prev) => ({
      pendingRequestAttachments: prev.pendingRequestAttachments.filter(
        (a) => a.uid !== uid
      ),
    }));
  };

  onPasteIntoForm = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    files.forEach((f) =>
      this.handleInitialUpload({
        file: Object.assign(f, { uid: `${Date.now()}-${Math.random()}` }),
      })
    );
  };

  // ===========================
  // Send message (with attachments)
  // ===========================
  sendMessage = async () => {
    const { t } = this.props;
    const { composer, selectedTicket, convoMessages, pendingAttachments } =
      this.state;

    // Disable chatting if closed
    if (isChatClosedStatus(selectedTicket?.status)) {
      message.warning(t("This request is closed."));
      return;
    }

    const msg = (composer || "").trim();
    const hasAttachments = pendingAttachments.length > 0;
    if (!msg && !hasAttachments) return;

    const attachmentsPayload = pendingAttachments.map(
      (a) => a.token || a.id || a.url
    );

    try {
      this.setState({ sending: true });
      const { data } = await instance.post("/send_request_message/", {
        tracking_id: selectedTicket.raw_id,
        message: msg,
        attachments: attachmentsPayload,
      });

      if (data?.success) {
        const newMsg = {
          key: `local-${Date.now()}`,
          created: new Date().toISOString(),
          sender: t("me"),
          isClient: true,
          message: msg,
          attachments: pendingAttachments.map((a) => ({
            url: a.url,
            name: a.name,
            size: a.size,
            mime: a.mime,
            id: a.id,
          })),
        };

        this.setState(
          {
            convoMessages: [...convoMessages, newMsg],
            composer: "",
            sending: false,
            pendingAttachments: [],
            uploadProgress: {},
          },
          this.scrollMessagesToBottom
        );
      } else {
        this.setState({ sending: false });
        message.error(t("Failed to send message."));
      }
    } catch (e) {
      this.setState({ sending: false });
      message.error(t("Failed to send message."));
    }
  };

  // ===========================
  // Attachment renderer (shared)
  // ===========================
  renderAttachmentBlock = (attachments, isMine, isRTL, locked = false) => {
    if (!attachments?.length) return null;
    const imgs = attachments.filter((a) => isImage(a.mime, a.name));
    const files = attachments.filter((a) => !isImage(a.mime, a.name));

    return (
      <div
        style={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {imgs.length > 0 && (
          <Image.PreviewGroup>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                gap: 8,
                direction: isRTL ? "rtl" : "ltr",
              }}
            >
              {imgs.map((a, i) => (
                <Image
                  key={`${a.url}-${i}`}
                  src={a.url}
                  alt={a.name}
                  width={120}
                  height={90}
                  style={{
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid #eee",
                  }}
                  preview={{ maskClassName: "ant-image-mask" }}
                />
              ))}
            </div>
          </Image.PreviewGroup>
        )}

        {files.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 8,
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            {files.map((a, i) => (
              <div
                key={`${a.url}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  border: "1px solid #eee",
                  borderRadius: 8,
                  background: "#fafafa",
                }}
              >
                <FileOutlined style={{ fontSize: 18, color: "black" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    title={a.name}
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      direction: "ltr",
                      textAlign: "left",
                      color: "black",
                    }}
                  >
                    {a.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#999" }}>
                    {formatBytes(a.size)}
                  </div>
                </div>
                <a href={a.url} target="_blank" rel="noreferrer">
                  <Tooltip title="Download">
                    <Button
                      icon={
                        <DownloadOutlined
                          style={{ position: "relative", top: -1 }}
                        />
                      }
                      size="small"
                    />
                  </Tooltip>
                </a>
                {!locked && <div style={{ width: 0, height: 0 }} />}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  renderMessageItem = (m) => {
    const isMine = !!m.isClient;
    const { config, t } = this.props;
    const isRTL = config?.direction === "rtl";

    const rowStyle = {
      display: "flex",
      justifyContent: isMine
        ? isRTL
          ? "flex-start"
          : "flex-end"
        : isRTL
        ? "flex-end"
        : "flex-start",
      marginBottom: 10,
      width: "100%",
    };

    const maxBubble = this.state.viewportWidth < 480 ? "86%" : "76%";
    const bubbleStyle = {
      maxWidth: maxBubble,
      background: isMine ? "#0099cc" : "#F5F5F5",
      color: isMine ? "white" : "black",
      padding: "10px 12px",
      borderRadius: 14,
      borderBottomLeftRadius: isMine ? (isRTL ? 6 : 14) : isRTL ? 14 : 6,
      borderBottomRightRadius: isMine ? (isRTL ? 14 : 6) : isRTL ? 6 : 14,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      fontSize: 13,
      lineHeight: 1.45,
      boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
      direction: isRTL ? "rtl" : "ltr",
      textAlign: isRTL ? "right" : "left",
    };

    const metaStyle = {
      fontSize: 11,
      color: "#999",
      margin: isMine
        ? isRTL
          ? "4px 0 0 6px"
          : "4px 6px 0 0"
        : isRTL
        ? "4px 6px 0 0"
        : "4px 0 0 6px",
      textAlign: isMine ? (isRTL ? "left" : "right") : isRTL ? "right" : "left",
      direction: isRTL ? "rtl" : "ltr",
    };

    return (
      <div key={m.key} style={rowStyle}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: isMine
              ? isRTL
                ? "flex-start"
                : "flex-end"
              : isRTL
              ? "flex-end"
              : "flex-start",
            width: "100%",
          }}
        >
          <div style={bubbleStyle}>
            {m.message}
            {this.renderAttachmentBlock(m.attachments, isMine, isRTL)}
          </div>
          <div style={metaStyle}>
            {m.sender
              ? `${m.sender === this.props.username ? t("me") : m.sender} • `
              : ""}
            {m.created ? moment(m.created).format("YYYY-MM-DD HH:mm") : ""}
          </div>
        </div>
      </div>
    );
  };

  // ===========================
  // Drawer (chat + initial attachments)
  // ===========================
  renderConversationDrawer = () => {
    const { t, config } = this.props;
    const {
      convoVisible,
      convoLoading,
      convoMessages,
      selectedTicket,
      composer,
      sending,
      viewportWidth,
      viewportHeight,
      pendingAttachments,
      dragOver,
      initialAttachments,
    } = this.state;

    const isRTL = config?.direction === "rtl";
    const drawerWidth = Math.min(620, viewportWidth - 24);
    const bodyHeight = Math.max(360, Math.min(820, viewportHeight - 180));
    const summaryMaxHeight = Math.max(180, Math.floor(bodyHeight * 0.45));

    const fullDescription =
      selectedTicket?._raw?.description ||
      selectedTicket?.description ||
      selectedTicket?._raw?.full_description ||
      selectedTicket?.tracking_id ||
      "";

    const dropStyles = dragOver
      ? {
          outline: "2px dashed #1890ff",
          outlineOffset: 4,
          background: "#f0f7ff",
          transition: "all .15s ease",
        }
      : {};

    const footerBg =
      "linear-gradient(180deg, rgba(250,250,250,1) 0%, rgba(245,245,245,1) 100%)";

    const chatDisabled = isChatClosedStatus(selectedTicket?.status);

    return (
      <Drawer
        width={drawerWidth}
        open={convoVisible}
        onClose={this.closeConversation}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: isRTL ? "flex-end" : "flex-start",
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 16 }}>
              {t("Ticket")} {selectedTicket?.raw_id}
            </span>
            <span
              style={{
                color: "#888",
                fontSize: 12,
                background: "#f5f5f5",
                padding: "2px 8px",
                borderRadius: 999,
              }}
            >
              {selectedTicket?.status
                ? t(
                    selectedTicket.status === "Rejected"
                      ? "Closed"
                      : selectedTicket.status
                  )
                : ""}
            </span>
          </div>
        }
        destroyOnClose
        maskClosable
        bodyStyle={{
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          height: bodyHeight,
          overflow: "hidden",
          direction: isRTL ? "rtl" : "ltr",
          textAlign: isRTL ? "right" : "left",
          background: "#fff",
        }}
        footer={
          <div
            style={{
              padding: 10,
              background: footerBg,
              borderTop: "1px solid #eee",
            }}
          >
            {chatDisabled && (
              <div
                style={{
                  marginBottom: 10,
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: "#fff7e6",
                  border: "1px solid #ffe7ba",
                  color: "#ad6800",
                  fontSize: 12,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("Chat is disabled because this request is closed.")}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                maxWidth: "100%",
                flexDirection: isRTL ? "row-reverse" : "row",
                opacity: chatDisabled ? 0.55 : 1,
                pointerEvents: chatDisabled ? "none" : "auto",
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 8,
                  borderRadius: 999,
                  background: "#fff",
                  boxShadow: "0 4px 12px rgba(0,0,0,.06)",
                  border: "1px solid #eaeaea",
                }}
              >
                <Upload
                  multiple
                  showUploadList={false}
                  customRequest={this.handleCustomUpload}
                  disabled={!selectedTicket || chatDisabled}
                >
                  <Tooltip title={t("Attach")}>
                    <Button
                      shape="circle"
                      icon={
                        <PaperClipOutlined
                          style={{ position: "relative", top: -1 }}
                        />
                      }
                      style={{ border: "none" }}
                      disabled={chatDisabled}
                    />
                  </Tooltip>
                </Upload>

                <div
                  style={{ flex: 1, position: "relative" }}
                  onDragOver={(e) => {
                    if (chatDisabled) return;
                    e.preventDefault();
                    if (!dragOver) this.setState({ dragOver: true });
                  }}
                  onDragLeave={() => this.setState({ dragOver: false })}
                  onDrop={this.onDropOnComposer}
                >
                  {!!pendingAttachments.length && (
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        marginBottom: 6,
                        padding: 6,
                        background: "#fafafa",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    >
                      {pendingAttachments.map((a) =>
                        isImage(a.mime, a.name) ? (
                          <div key={a.uid} style={{ position: "relative" }}>
                            <Image
                              src={a.url}
                              alt={a.name}
                              width={72}
                              height={54}
                              style={{
                                objectFit: "cover",
                                borderRadius: 6,
                                border: "1px solid #eee",
                              }}
                              preview={{ src: a.url }}
                            />
                            <CloseCircleFilled
                              onClick={() =>
                                this.removePendingAttachment(a.uid)
                              }
                              style={{
                                position: "absolute",
                                top: -8,
                                right: -8,
                                fontSize: 18,
                                color: "#ff4d4f",
                                cursor: "pointer",
                              }}
                            />
                            {this.state.uploadProgress[a.uid] >= 0 &&
                              this.state.uploadProgress[a.uid] < 100 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: 2,
                                    left: 2,
                                    right: 2,
                                    background: "rgba(255,255,255,.8)",
                                    borderRadius: 4,
                                    padding: "2px 4px",
                                  }}
                                >
                                  <Progress
                                    percent={this.state.uploadProgress[a.uid]}
                                    size="small"
                                    showInfo={false}
                                  />
                                </div>
                              )}
                          </div>
                        ) : (
                          <div
                            key={a.uid}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 8px",
                              border: "1px solid #eee",
                              borderRadius: 6,
                              background: "white",
                              maxWidth: 260,
                            }}
                          >
                            <FileOutlined />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                title={a.name}
                                style={{
                                  fontSize: 13,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  direction: "ltr",
                                  textAlign: "left",
                                }}
                              >
                                {a.name}
                              </div>
                              <div style={{ fontSize: 12, color: "#999" }}>
                                {formatBytes(a.size)}
                              </div>
                            </div>
                            <Tooltip title={t("Remove")}>
                              <Button
                                size="small"
                                icon={
                                  <DeleteOutlined
                                    style={{ position: "relative", top: -1 }}
                                  />
                                }
                                onClick={() =>
                                  this.removePendingAttachment(a.uid)
                                }
                              />
                            </Tooltip>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  <TextArea
                    value={composer}
                    disabled={chatDisabled}
                    onChange={(e) =>
                      this.setState({ composer: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                        e.preventDefault();
                        this.sendMessage();
                      }
                    }}
                    onPaste={this.onPasteIntoComposer}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        this.sendMessage();
                      }
                    }}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    placeholder={
                      chatDisabled
                        ? t("Chat disabled for closed requests.")
                        : t("Write a message…")
                    }
                    style={{
                      flex: 1,
                      border: "none",
                      boxShadow: "none",
                      direction: isRTL ? "rtl" : "ltr",
                      textAlign: isRTL ? "right" : "left",
                      background: "transparent",
                      ...dropStyles,
                    }}
                  />
                </div>

                <Button
                  type="primary"
                  shape="round"
                  icon={<SendOutlined />}
                  onClick={this.sendMessage}
                  loading={sending}
                  disabled={
                    chatDisabled ||
                    (!composer.trim() && !pendingAttachments.length)
                  }
                >
                  {t("Send")}
                </Button>
              </div>
            </div>
          </div>
        }
        footerStyle={{
          border: "none",
          padding: 0,
          background: "#fafafa",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <div
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 10,
            background: "#fafafa",
            padding: 12,
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: summaryMaxHeight,
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <p style={{ marginBottom: 4 }}>
            <span style={{ fontWeight: 700 }}>{t("Topic")}</span>:{" "}
            {t(selectedTicket?._raw?.topic)}
            {!!selectedTicket?._raw?.subtopic ? (isRTL ? " -" : " -") : ""}{" "}
            {t(selectedTicket?._raw?.subtopic)}
          </p>
          <p className="bold mb-0" style={{ marginTop: 6, fontWeight: 700 }}>
            {t("Description")}
          </p>
          {fullDescription || t("No description")}

          {initialAttachments?.length > 0 && (
            <>
              <Divider style={{ margin: "10px 0" }} />
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <PictureOutlined style={{ position: "relative", top: -1 }} />
                {t("Attachments (submitted with request)")}
              </div>
              {this.renderAttachmentBlock(
                initialAttachments,
                false,
                isRTL,
                true
              )}
            </>
          )}
        </div>

        <p
          className="bold mb-1"
          style={{ marginTop: 8, textAlign: isRTL ? "right" : "left" }}
        >
          {t("Comments")}
        </p>

        <div
          ref={this.messagesWrapRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: 10,
            border: "1px solid #f0f0f0",
            borderRadius: 10,
            background: "#fff",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          {convoLoading ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spin />
            </div>
          ) : convoMessages.length ? (
            convoMessages.map(this.renderMessageItem)
          ) : (
            <div style={{ padding: 24, color: "#999" }}>
              {t("No messages yet")}
            </div>
          )}
        </div>
      </Drawer>
    );
  };

  // ===========================
  // Requests list
  // ===========================
  renderList = () => {
    const { t, config } = this.props;
    const { requests, loadingRequests } = this.state;
    const isRTL = config?.direction === "rtl";

    const allStatusesForFilters = Array.from(
      new Set([...TECH_STATUSES, ...SUPPORT_STATUSES])
    );

    const columns = [
      {
        title: t("Type"),
        dataIndex: "department",
        filters: ["tech", "success"].map((s) => ({
          text: t({ tech: "Technical", success: "Support" }[s]),
          value: s,
        })),
        onFilter: (value, record) => record.department === value,
        width: 160,
        key: "department",
        render: (v) => (
          <div style={{ textAlign: "center", fontSize: 12 }}>
            {v === "tech" ? t("Technical") : t("Support")}
          </div>
        ),
      },
      {
        title: t("Ticket"),
        dataIndex: "tracking_id",
        key: "tracking_id",
        width: 520,
        render: (v, record) => (
          <div
            onClick={() => {
              this.openConversation(record);
              this.props.history.push("/manage/requests/all/" + record.raw_id);
            }}
            style={{
              textAlign: isRTL ? "right" : "left",
              color: "#0099cc",
              cursor: "pointer",
              fontWeight: "bold",
              wordBreak: "break-word",
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            <Badge
              dot
              count={record.has_notifications ? 1 : 0}
              style={{
                right: !isRTL && -5,
                left: isRTL && -5,
                color: "#0099cc",
              }}
            >
              <span style={{ color: "#0099cc" }}>{v}</span>
            </Badge>
          </div>
        ),
      },
      {
        title: t("Created"),
        dataIndex: "created",
        width: 160,
        key: "created",
        sorter: (a, b) => new Date(a.created || 0) - new Date(b.created || 0),
        render: (v) => (
          <div style={{ textAlign: "center", fontSize: 12, color: "gray" }}>
            {v ? moment(v).format("YYYY-MM-DD HH:mm") : "-"}
          </div>
        ),
      },
      {
        title: t("Last activity"),
        dataIndex: "modified",
        key: "modified",
        width: 160,
        sorter: (a, b) => new Date(a.modified || 0) - new Date(b.modified || 0),
        render: (v) => (
          <div style={{ textAlign: "center", fontSize: 12, color: "gray" }}>
            {v ? moment(v).format("YYYY-MM-DD HH:mm") : "-"}
          </div>
        ),
      },
      {
        title: t("Status"),
        dataIndex: "status",
        key: "status",
        width: 220,
        filters: allStatusesForFilters.map((s) => ({
          text: t(s === "Rejected" ? "Closed" : s),
          value: s,
        })),
        onFilter: (value, record) => record.status === value,
        render: (s, record) => {
          const support = isSupportDept(record.department);
          const canEdit = support && this.props.is_staff;
          const label = !support && s === "Rejected" ? t("Closed") : t(s);

          if (!canEdit) {
            return (
              <div style={{ textAlign: "center" }}>
                <Tag color={getStatusColor(record.department, s)}>{label}</Tag>
              </div>
            );
          }

          const options = getStatusesForDept(record.department);
          const updating = !!this.state.statusUpdating?.[record.raw_id];

          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Select
                size="small"
                value={s}
                loading={updating}
                disabled={updating}
                onChange={(v) => this.changeRequestStatus(record, v)}
                style={{ minWidth: 170 }}
                dropdownMatchSelectWidth={false}
              >
                {options.map((st) => (
                  <Select.Option key={st} value={st}>
                    {t(st)}
                  </Select.Option>
                ))}
              </Select>
            </div>
          );
        },
      },
    ];

    return (
      <div dir={isRTL ? "rtl" : "ltr"}>
        <Space
          style={{
            marginBottom: 12,
            display: "flex",
            justifyContent: isRTL ? "flex-start" : "space-between",
            width: "100%",
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          <Button onClick={this.fetchRequests}>{t("Refresh")}</Button>
        </Space>

        <Table
          columns={columns}
          dataSource={requests}
          loading={loadingRequests}
          pagination={{ pageSize: 8, showSizeChanger: false, responsive: true }}
          size="middle"
          rowKey="key"
          scroll={{ x: 900 }}
        />

        {this.renderConversationDrawer()}
      </div>
    );
  };

  // ===========================
  // Priority UI
  // ===========================
  renderPrioritySection = () => {
    const { t, config } = this.props;
    const { priority } = this.state;
    const isRTL = config?.direction === "rtl";

    const PRIORITY_PILLS = [
      { value: "Low", label: t("Not Urgent") },
      { value: "Medium", label: t("Soon, Please") },
      { value: "High", label: t("Urgent") },
    ];

    return (
      <div className="SupportReq__field" dir={isRTL ? "rtl" : "ltr"}>
        <label className="SupportReq__label">{t("Priority")}</label>

        <div
          className="SupportReqPriority"
          style={{ direction: isRTL ? "rtl" : "ltr" }}
          role="radiogroup"
          aria-label="Priority"
        >
          {PRIORITY_PILLS.map((p) => {
            const active = priority === p.value;

            return (
              <button
                key={p.value}
                type="button"
                className={`SupportReqPriority__item ${
                  active ? "isActive" : ""
                }`}
                onClick={() => this.setState({ priority: p.value })}
                role="radio"
                aria-checked={active}
              >
                <span className="SupportReqPriority__label">{p.label}</span>

                <span
                  className={`SupportReqPriority__box ${
                    active ? "isActive" : ""
                  }`}
                >
                  {active ? <CheckOutlined /> : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ===========================
  // Form (unchanged)
  // ===========================
  renderForm = () => {
    const { config, t } = this.props;
    const {
      topic,
      subtopic,
      description,
      submitting,
      type,
      pendingRequestAttachments,
      requestUploadProgress,
      priority,
      first_response,
    } = this.state;

    const isRTL = config?.direction === "rtl";
    const subtopics = this.requiresSubtopic(topic) ? SUBTOPICS[topic] : [];

    const isSupport = this.props.history?.location?.pathname?.includes(
      "/requests/new_support"
    );

    return (
      <div className="SupportReq" dir={isRTL ? "rtl" : "ltr"}>
        <div className="SupportReq__titleBlock">
          <div className="SupportReq__title">
            {isSupport ? t("Support Request Form") : t("Technical Issue Form")}
          </div>
          <div className="SupportReq__subtitle">
            {isSupport
              ? t("Get help with onboarding, billing, or your account.")
              : t("Help us understand what’s not working.")}
          </div>
        </div>

        {!isSupport && (
          <div className="SupportReq__field">
            <label className="SupportReq__label">
              {t("Issue Type")} <span className="SupportReq__req">*</span>
            </label>

            <Select
              size="large"
              value={type}
              onChange={(v) => this.setState({ type: v })}
              placeholder={t("Choose a topic")}
              style={{ width: "100%" }}
              direction={isRTL ? "rtl" : "ltr"}
            >
              <Select.Option
                value="bug"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("Bug / Something isn’t working")}
              </Select.Option>
              <Select.Option
                value="feature"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("Feature request / improvement")}
              </Select.Option>
              <Select.Option
                value="performance"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("Performance issue")}
              </Select.Option>
              <Select.Option
                value="integration"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("Integration issue")}
              </Select.Option>
            </Select>
          </div>
        )}

        <div className="SupportReq__field">
          <label className="SupportReq__label">
            {isSupport ? t("What Do You Need Help with?") : t("Affected Area")}{" "}
            <span className="SupportReq__req">*</span>
          </label>

          <Select
            size="large"
            placeholder={t("Choose a topic")}
            style={{ width: "100%" }}
            value={topic}
            onChange={(v) => this.setState({ topic: v, subtopic: undefined })}
            showSearch
            optionFilterProp="children"
            dropdownMatchSelectWidth
            direction={isRTL ? "rtl" : "ltr"}
          >
            {(isSupport ? SUPPORT_TOPICS : TOPICS).map((x) => (
              <Select.Option
                key={x}
                value={x}
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t(x)}
              </Select.Option>
            ))}
          </Select>
        </div>

        {this.requiresSubtopic(topic) && (
          <div className="SupportReq__field">
            <label className="SupportReq__label">
              {t("Sub-topic")} <span className="SupportReq__req">*</span>
            </label>

            <Select
              size="large"
              placeholder={t("Choose a topic")}
              style={{ width: "100%" }}
              value={subtopic}
              onChange={(v) => this.setState({ subtopic: v })}
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth
              direction={isRTL ? "rtl" : "ltr"}
            >
              {subtopics.map((x) => (
                <Select.Option
                  key={x}
                  value={x}
                  style={{ textAlign: isRTL ? "right" : "left" }}
                >
                  {t(x)}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        <div className="SupportReq__field">
          <label className="SupportReq__label">
            {isSupport ? t("Message") : t("Description")}{" "}
            <span className="SupportReq__req">*</span>
          </label>

          <TextArea
            rows={5}
            maxLength={DESCRIPTION_MAX_LENGTH}
            showCount
            placeholder={
              isSupport
                ? t("Tell us what you need help with...")
                : t("What happened? Please be as detailed as possible.")
            }
            value={description}
            onChange={(e) =>
              this.setState({
                description: e.target.value.slice(0, DESCRIPTION_MAX_LENGTH),
              })
            }
            onPaste={this.onPasteIntoForm}
            className="SupportReq__textarea"
            style={{
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          />

          <div className="SupportReq__hint" style={{ marginTop: 4 }}>
            {t(
              "Include details on steps, error messages, or examples help us fix this faster."
            )}
          </div>
        </div>

        <div className="SupportReq__field">
          <label className="SupportReq__label">
            {t("Attachments (Optional)")}
          </label>

          <Dragger
            multiple
            showUploadList={false}
            customRequest={this.handleInitialUpload}
            className="SupportReq__dragger"
          >
            <div className="SupportReq__draggerRow">
              <div>
                <div
                  className="SupportReq__draggerTitle"
                  style={{
                    textAlign: config.direction === "rtl" ? "right" : "left",
                  }}
                >
                  {t("Drop Files Here")}
                </div>
              </div>
              <PaperClipOutlined className="SupportReq__clip" />
            </div>
          </Dragger>

          <div className="SupportReq__draggerHint">
            {t("You can also paste images/files into the description box.")}
          </div>

          {!!pendingRequestAttachments.length && (
            <div
              className="SupportReq__tray"
              style={{ direction: isRTL ? "rtl" : "ltr" }}
            >
              {pendingRequestAttachments.map((a) =>
                isImage(a.mime, a.name) ? (
                  <div key={a.uid} className="SupportReq__imgWrap">
                    <Image
                      src={a.url}
                      alt={a.name}
                      width={92}
                      height={68}
                      style={{ objectFit: "cover", borderRadius: 10 }}
                      preview={{ src: a.url }}
                    />
                    <CloseCircleFilled
                      onClick={() => this.removeInitialPending(a.uid)}
                      className="SupportReq__remove"
                    />
                    {requestUploadProgress[a.uid] >= 0 &&
                      requestUploadProgress[a.uid] < 100 && (
                        <div className="SupportReq__progress">
                          <Progress
                            percent={requestUploadProgress[a.uid]}
                            size="small"
                            showInfo={false}
                          />
                        </div>
                      )}
                  </div>
                ) : (
                  <div key={a.uid} className="SupportReq__fileChip">
                    <FileOutlined />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="SupportReq__fileName" title={a.name}>
                        {a.name}
                      </div>
                      <div className="SupportReq__fileSize">
                        {formatBytes(a.size)}
                      </div>
                    </div>
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => this.removeInitialPending(a.uid)}
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {this.renderPrioritySection()}

        {this.props.is_staff && (
          <div className="SupportReq__staff">
            <Divider style={{ margin: "18px 0" }} />
            <div className="SupportReq__staffTitle">{t("For staff only")}</div>

            <Col span={24}>
              <div
                className="mb-1"
                style={{ fontWeight: 500, textAlign: isRTL ? "right" : "left" }}
              >
                {t("Urgency")}
              </div>
              <Select
                placeholder={t("Urgency")}
                style={{ width: "100%" }}
                value={first_response}
                onChange={(v) => this.setState({ first_response: v })}
                direction={isRTL ? "rtl" : "ltr"}
              >
                {FIRST_RESPONSE_OPTIONS.map((x) => (
                  <Option
                    key={x}
                    value={x}
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    {t(x)}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col span={24}>
              <div
                className="mb-1"
                style={{ fontWeight: 500, textAlign: isRTL ? "right" : "left" }}
              >
                {t("Submit on behalf of")}
              </div>
              <Select
                placeholder={t("Select a user")}
                style={{ width: "100%" }}
                value={this.state.selectedUser}
                onChange={(v) => this.setState({ selectedUser: v })}
                direction={isRTL ? "rtl" : "ltr"}
              >
                {this.state.users.map((x) => (
                  <Option
                    key={x.user__username}
                    value={x.user__username}
                    disabled={!x.did_verify_phone}
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    {x.user__username}{" "}
                    {!x.did_verify_phone ? `(${t("phone not verified")})` : ""}
                  </Option>
                ))}
              </Select>
            </Col>
          </div>
        )}

        <div className="SupportReq__bottomBar">
          <Button
            type="primary"
            size="large"
            loading={submitting}
            onClick={this.handleSubmit}
            disabled={
              !topic ||
              !description?.trim() ||
              !priority ||
              (this.requiresSubtopic(topic) && !subtopic) ||
              !type
            }
            style={{
              width: "100%",
              height: 45,
              borderRadius: 14,
              background: "#0099cc",
              borderColor: "#0099cc",
              fontWeight: 800,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              boxShadow: "0 10px 22px rgba(0, 153, 204, 0.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingInlineStart: 18,
              paddingInlineEnd: 18,
            }}
          >
            <span
              style={{
                flex: 1,
                textAlign: config.direction === "rtl" ? "right" : "left",
                color: "white",
                fontSize: 13,
              }}
            >
              {t("SEND REQUEST")}
            </span>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 10,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                marginInlineStart: 12,
              }}
            >
              <SendOutlined
                style={{
                  fontSize: 16,
                  color: "white",
                  position: "relative",
                  top: 0,
                  transform: config.direction === "rtl" ? "scaleX(-1)" : "",
                }}
              />
            </span>
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const { config, t } = this.props;
    const isRTL = config?.direction === "rtl";
    const isSupport = this.props.history?.location?.pathname?.includes(
      "/requests/new_support"
    );

    return (
      <ConfigProvider direction={isRTL ? "rtl" : "ltr"}>
        <div dir={isRTL ? "rtl" : "ltr"}>
          <Modal
            style={{ top: 20 }}
            destroyOnClose
            footer={null}
            closable={false}
            visible={true}
            onCancel={() => {
              if (this.state.lastPath)
                this.props.history.replace(this.state.lastPath);
              else this.props.history.replace("/manage/home");
            }}
            bodyStyle={{ padding: 0 }}
            width={this.state.activeTab === "list" ? 1000 : 650}
            className="SupportReqModal"
          >
            <div
              className="SupportReqModal__wrap"
              dir={isRTL ? "rtl" : "ltr"}
              style={{
                textAlign: config.direction === "rtl" ? "right" : "left",
              }}
            >
              <button
                className="SupportReqModal__close"
                style={{
                  [config.direction === "rtl" ? "left" : "right"]: 16,
                }}
                onClick={() => {
                  if (this.state.lastPath)
                    this.props.history.replace(this.state.lastPath);
                  else this.props.history.replace("/manage/home");
                }}
                aria-label="Close"
              >
                ×
              </button>

              <div className="SupportReqModal__crumbs">
                <span
                  className="SupportReqModal__crumbMuted"
                  style={{ cursor: "pointer" }}
                  onClick={() => this.props.history.replace("/manage/help")}
                >
                  <SmileOutlined
                    style={{ width: 20, height: 20, fill: "gray" }}
                  />{" "}
                  {t("Help")}
                </span>
                <span className="SupportReqModal__sep">›</span>
                <span className="SupportReqModal__crumbStrong">
                  {isSupport ? <UserOutlined /> : <SettingOutlined />}{" "}
                  {isSupport
                    ? t("Contact Your Account Manager")
                    : t("Report A Technical Issue")}
                </span>
              </div>

              {this.state.activeTab === "new" ? (
                this.renderForm()
              ) : (
                <div style={{ paddingTop: 10 }}>{this.renderList()}</div>
              )}
            </div>
          </Modal>
        </div>
      </ConfigProvider>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    requestsNotificationsOff: () =>
      dispatch({ type: "TURN_OFF_R_NOTIFICATIONS" }),
  };
};

const mapStateToProps = (state) => ({
  config: state.config,
  is_staff: state.auth.is_staff,
  username: state.auth.username,
  isHelpOpen: state.orders.isHelpOpen,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withNamespaces()(FeatureRequestModal));
